import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import TavolinoBadge from "@/components/TavolinoBadge";

/**
 * RecentResults — public feed of last played matches (league + cups).
 * Used on the landing page to engage visitors.
 */
export default function RecentResults({ limit = 8 }) {
    const [matches, setMatches] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/feed/recent-matches", { params: { limit } });
                setMatches(data?.matches || []);
            } catch (e) {
                // silent
            } finally { setLoading(false); }
        })();
    }, [limit]);

    if (loading) {
        return <div className="op-label text-t8f95a3 animate-pulse">// CARICAMENTO RISULTATI…</div>;
    }
    if (matches.length === 0) {
        return (
            <div className="op-card p-8 text-center">
                <div className="text-3xl mb-3">⏱️</div>
                <div className="op-label text-t8f95a3">
                    Ancora nessuna partita giocata. Torna presto per gli ultimi risultati.
                </div>
            </div>
        );
    }

    return (
        <div className="grid md:grid-cols-2 gap-4" data-testid="recent-results">
            {matches.map((m) => (
                <ResultCard key={`${m.kind}-${m.match_id}`} m={m} />
            ))}
        </div>
    );
}

function ResultCard({ m }) {
    const home = m.home_team || { name: "—" };
    const away = m.away_team || { name: "—" };
    const hs = m.home_score ?? 0;
    const as_ = m.away_score ?? 0;
    const homeWon = hs > as_;
    const awayWon = as_ > hs;

    return (
        <Link
            to={m.context_link}
            className="op-card p-4 block hover:border-t2bb6ff transition-colors"
            data-testid={`result-${m.match_id}`}
            style={{ borderLeft: `3px solid ${m.context_color}` }}
        >
            <div
                className="op-label text-[10px] mb-3"
                style={{ color: m.context_color }}
            >
                {m.context_label}
            </div>

            {m.auto_result && (
                <div className="mb-2"><TavolinoBadge idSuffix={m.match_id} definitive={m.is_definitive} /></div>
            )}

            <div className="flex items-center gap-3">
                <div className="flex-1 flex items-center justify-end gap-2 min-w-0">
                    <span className={`text-sm truncate ${homeWon ? "text-tffffff font-bold" : "text-t8f95a3"}`}>
                        {home.name}
                    </span>
                    {home.logo_url && (
                        <img src={home.logo_url} alt="" className="w-8 h-8 object-contain flex-shrink-0" />
                    )}
                </div>
                <div
                    className="op-mono text-2xl font-bold px-4 whitespace-nowrap"
                    style={{ color: m.context_color }}
                >
                    {hs} <span className="text-t5c6370">-</span> {as_}
                </div>
                <div className="flex-1 flex items-center gap-2 min-w-0">
                    {away.logo_url && (
                        <img src={away.logo_url} alt="" className="w-8 h-8 object-contain flex-shrink-0" />
                    )}
                    <span className={`text-sm truncate ${awayWon ? "text-tffffff font-bold" : "text-t8f95a3"}`}>
                        {away.name}
                    </span>
                </div>
            </div>

            {/* Scorers + MVP */}
            {(m.scorers?.length > 0 || m.mvp) && (
                <div className="mt-3 pt-3 border-t border-t22252a flex flex-wrap gap-x-4 gap-y-1 text-[11px]">
                    {m.scorers?.length > 0 && (
                        <div className="flex items-center gap-1.5 text-tc9ccd3">
                            <span className="op-label text-[9px] text-tff7a1a">⚽</span>
                            <span className="truncate">
                                {m.scorers.map((s) => s.name + (s.goals > 1 ? ` (${s.goals})` : "")).join(", ")}
                            </span>
                        </div>
                    )}
                    {m.mvp && (
                        <div className="flex items-center gap-1.5 text-te8ff00">
                            <span className="op-label text-[9px]">⭐</span>
                            <span className="truncate">{m.mvp.name}</span>
                        </div>
                    )}
                </div>
            )}
        </Link>
    );
}
